import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
  DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Compass } from "@phosphor-icons/react";
import { toast } from "sonner";

const PATHS = [
  { value: "not_decided",   label: "Not decided yet",   color: "bg-slate-100 text-slate-700" },
  { value: "in_house",      label: "In-house design",   color: "bg-emerald-100 text-emerald-800" },
  { value: "outsourced",    label: "Outsourced design", color: "bg-indigo-100 text-indigo-800" },
  { value: "not_applicable", label: "Not applicable",   color: "bg-zinc-200 text-zinc-700" },
];

export function DesignInputsBadge({ designPath }) {
  const p = PATHS.find((x) => x.value === designPath) || PATHS[0];
  return (
    <span className={`inline-block rounded px-2 py-0.5 text-[10px] font-mono-alt uppercase tracking-wider ${p.color}`}
          title="Design path · click the project to manage Design Inputs (F-PM-02)">
      Design · {p.label}
    </span>
  );
}

export default function DesignInputsDrawer({ project, trigger, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    design_path: "not_decided",
    client_brief: "", contract_ref: "", sow: "", tor: "",
    design_criteria: "", codes_standards: "", statutory_reqs: "",
    schedule_ref: "", internal_responsibility: "",
    decision_rationale: "", deliverable_outputs: "",
  });

  useEffect(() => {
    if (!open) return;
    api.get(`/projects/${project.id}/design-inputs`).then(({ data }) => {
      const di = data.design_inputs || {};
      setForm({ design_path: data.design_path || "not_decided", ...di });
    }).catch((err) => toast.error(formatApiError(err)));
  }, [open, project.id]);

  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      await api.put(`/projects/${project.id}/design-inputs`, form);
      toast.success("Design inputs saved");
      onChanged?.();
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Compass size={18} /> Design Inputs · {project.name}
          </DialogTitle>
          <DialogDescription>
            Per DESCON Design Development SOP. Capture client brief, SOW, ToR,
            codes/standards, statutory requirements, and decide design path
            (in-house vs outsourced). Outsourced path will route through
            Procurement.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={save} className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label>Design path *</Label>
            <Select value={form.design_path} onValueChange={(v) => setField("design_path", v)}>
              <SelectTrigger data-testid="design-path"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PATHS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {form.design_path === "outsourced" && (
              <p className="mt-1.5 text-xs text-indigo-700">
                ⓘ For outsourced design, finalize SOW + ToR here, then raise a
                procurement request from the relevant deliverable to appoint
                the Design Engineering Subcontractor.
              </p>
            )}
          </div>

          <div className="col-span-2">
            <Label>Client brief</Label>
            <Textarea rows={2} value={form.client_brief}
              onChange={(e) => setField("client_brief", e.target.value)}
              placeholder="What the client wants — problem statement, success criteria…"
              data-testid="client-brief" />
          </div>
          <div>
            <Label>Contract reference</Label>
            <Input value={form.contract_ref}
              onChange={(e) => setField("contract_ref", e.target.value)}
              placeholder="DESCON-CON-2026-007" />
          </div>
          <div>
            <Label>Schedule reference</Label>
            <Input value={form.schedule_ref}
              onChange={(e) => setField("schedule_ref", e.target.value)}
              placeholder="Master schedule v2.1 / milestone M3" />
          </div>

          <div className="col-span-2">
            <Label>Scope of Work (SOW)</Label>
            <Textarea rows={3} value={form.sow}
              onChange={(e) => setField("sow", e.target.value)}
              placeholder="Bullet the scope to be delivered…"
              data-testid="sow" />
          </div>
          <div className="col-span-2">
            <Label>Terms of Reference (ToR)</Label>
            <Textarea rows={3} value={form.tor}
              onChange={(e) => setField("tor", e.target.value)}
              placeholder="Objectives, expected outcomes, qualifications, reporting frequency…"
              data-testid="tor" />
          </div>
          <div className="col-span-2">
            <Label>Design criteria</Label>
            <Textarea rows={2} value={form.design_criteria}
              onChange={(e) => setField("design_criteria", e.target.value)}
              placeholder="Loads, service life, environmental conditions, hydraulic head…" />
          </div>
          <div>
            <Label>Codes & standards</Label>
            <Input value={form.codes_standards}
              onChange={(e) => setField("codes_standards", e.target.value)}
              placeholder="Eurocode 2 · BS EN 1992 · ACI 318" />
          </div>
          <div>
            <Label>Statutory / local rules</Label>
            <Input value={form.statutory_reqs}
              onChange={(e) => setField("statutory_reqs", e.target.value)}
              placeholder="Federal Roads Authority · IFC EHS · UNOPS HSE" />
          </div>
          <div>
            <Label>Internal responsibility</Label>
            <Input value={form.internal_responsibility}
              onChange={(e) => setField("internal_responsibility", e.target.value)}
              placeholder="Lead engineer · email" />
          </div>
          <div>
            <Label>Expected design outputs</Label>
            <Input value={form.deliverable_outputs}
              onChange={(e) => setField("deliverable_outputs", e.target.value)}
              placeholder="Drawings · BoQ · Reports · Models" />
          </div>
          <div className="col-span-2">
            <Label>Path decision rationale</Label>
            <Textarea rows={2} value={form.decision_rationale}
              onChange={(e) => setField("decision_rationale", e.target.value)}
              placeholder="Why in-house or outsourced? Capacity? Specialist need? Risk?" />
          </div>

          <DialogFooter className="col-span-2">
            <Button type="submit" disabled={busy} className="btn-primary-blue"
                     data-testid="save-design-inputs">
              {busy ? "Saving…" : "Save design inputs"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
